import { useEffect, useRef, useState } from 'react';
import { getGatewayHttpUrl } from '../auth/serviceToken.js';
import { requestSpectatorPass } from './spectateApi.js';
import { SpectateFeed, type SpectateFrame, type SpectateHeader } from './spectateFeed.js';

export type SpectatePhase = 'pass' | 'connecting' | 'watching' | 'error';
export type SpectateError = 'signed-out' | 'no-such-match' | 'unreachable' | 'rejected';

export interface SpectateSession {
  readonly phase: SpectatePhase;
  readonly error: SpectateError | null;
  readonly header: SpectateHeader | null;
  readonly serverUrl: string;
  readonly feed: SpectateFeed;
  /** `match:end` arrived: the winner's seat account id ('' = none). */
  readonly ended: { readonly winnerId: string } | null;
}

/**
 * Get a pass, dial the match-server, join as a spectator. Frames are handed to
 * `feed`; the caller builds the sim from `header` and attaches. Nothing this
 * hook does can send an order.
 */
export function useSpectateSession(matchId: string): SpectateSession {
  const feedRef = useRef(new SpectateFeed());
  const [phase, setPhase] = useState<SpectatePhase>('pass');
  const [error, setError] = useState<SpectateError | null>(null);
  const [header, setHeader] = useState<SpectateHeader | null>(null);
  const [serverUrl, setServerUrl] = useState('');
  const [ended, setEnded] = useState<{ winnerId: string } | null>(null);

  useEffect(() => {
    let closed = false;
    let ws: WebSocket | null = null;
    const fail = (e: SpectateError) => {
      if (closed) return;
      setError(e);
      setPhase('error');
    };
    const gw = getGatewayHttpUrl();
    if (!gw) {
      fail('unreachable');
      return;
    }
    requestSpectatorPass(gw, matchId).then((res) => {
      if (closed) return;
      if (!res.ok) return fail(res.reason);
      setServerUrl(res.pass.serverUrl);
      setPhase('connecting');
      ws = new WebSocket(res.pass.serverUrl);
      ws.addEventListener('open', () => {
        ws?.send(JSON.stringify({ type: 'spectator:join', token: res.pass.token }));
      });
      ws.addEventListener('error', () => fail('unreachable'));
      ws.addEventListener('message', (e) => {
        let msg: Record<string, unknown>;
        try {
          msg = JSON.parse(e.data as string);
        } catch {
          return;
        }
        if (msg.type === 'spectate:ack') {
          feedRef.current.push((msg.frames as SpectateFrame[]) ?? []);
          setHeader(msg.header as SpectateHeader);
          setPhase('watching');
          if (msg.ended) setEnded({ winnerId: '' });
        } else if (msg.type === 'input:frame') {
          feedRef.current.push([msg as unknown as SpectateFrame]);
        } else if (msg.type === 'match:end') {
          setEnded({ winnerId: String(msg.winnerId ?? '') });
        } else if (msg.type === 'error') {
          fail('rejected');
        }
      });
    });
    return () => {
      closed = true;
      ws?.close();
    };
  }, [matchId]);

  return { phase, error, header, serverUrl, feed: feedRef.current, ended };
}
